import React, { useMemo, useState } from 'react';
import { JSONToExcel } from 'src/util/fileUtil';
import { dataMapper } from './hooks/utils';
import { StyledText } from 'src/components/StyledTypography';
import { useTranslate } from '@im/react-localization';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import AuditLogFilters from './components/AuditLogsFilters';
import {
  AuditLogSearchQuery,
  buildAuditSearchRequest,
  useGetGdsAuditLogs,
} from './hooks/useAuditLogsHooks';
import { useAuditLogColumns, Rows } from './components/AuditLogsColumns';
import CustomTable from 'src/components/TableComponent/TableComponent';
import { Pagination } from '@im/ui-lib/Pagination';
import { useGetBemAuditLogs } from '../bidManagementHub/hooks/apiHooks';

export default function LogsList() {
  const translate = useTranslate();
  const [searchQuery, setSearchQuery] = useState<AuditLogSearchQuery>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });
  const columns = useAuditLogColumns(searchQuery.Category);

  const isBem = searchQuery.Category === 'BEM';
  const request = buildAuditSearchRequest(searchQuery, {
    pageNumber: pagination.page,
    pageSize: pagination.pageSize,
  });

  const gdsAudit = useGetGdsAuditLogs(
    request,
    !isBem && hasSearched && !!searchQuery.Category
  );
  const bemAudit = useGetBemAuditLogs(
    request,
    isBem && hasSearched && !!searchQuery.Category
  );

  const handleSearch = (latestSearchQuery?: AuditLogSearchQuery) => {
    setHasSearched(true);
    setPagination({ page: 1, pageSize: pagination.pageSize });
    setSearchQuery(latestSearchQuery || searchQuery);
  };

  const auditData = isBem ? bemAudit.data?.response : gdsAudit.data?.response;
  const loading = isBem ? bemAudit.isLoading : gdsAudit.isLoading;

  const rows: Rows[] = useMemo(
    () =>
      auditData?.map((item, index) => ({
        id: index + 1,
        ...item,
      })) ?? [],
    [auditData]
  );

  const downloadLogs = () => {
    JSONToExcel(dataMapper(rows), 'AuditLogs.xlsx');
  };

  const requestPagination = () => (
    <Pagination
      data-testid="gds-audit-logs-table-pagination"
      onPageChange={(page) => setPagination({ ...pagination, page })}
      onRowsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
      page={pagination.page}
      rowsPerPage={pagination.pageSize}
      totalCount={
        isBem
          ? bemAudit.data?.pageDetails.totalRecords ?? 0
          : gdsAudit.data?.pageDetails.totalRecords ?? 0
      }
    />
  );

  return (
    <Stack p={2} spacing={2}>
      <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
        <StyledText
          sx={{
            alignContent: 'center',
            display: 'grid',
            fontSize: '26px',
            fontWeight: 600,
          }}
        >
          {translate('AuditLogs.PageTitle')}
        </StyledText>

        <Button
          data-testid="gds-audit-logs-download-logs-btn"
          disabled={rows.length === 0 || loading}
          onClick={downloadLogs}
          variant="contained"
        >
          {translate('AuditLogs.DownloadLogs')}
        </Button>
      </Stack>

      <AuditLogFilters
        onSearch={handleSearch}
        setSearchQuery={setSearchQuery}
      />

      <CustomTable
        columns={columns}
        data={rows}
        getRowId={(row) => row.id.toString()}
        isLoading={loading}
        paginationComponent={requestPagination()}
        sx={{
          '& .MuiTableCell-head': {
            height: 50,
          },
          '.MuiTableBody-root': {
            '.MuiTableRow-root': { verticalAlign: 'baseline' },
          },
          '.MuiTableCell-root': { px: 1 },
        }}
      />
    </Stack>
  );
}
